import React, { Suspense, useEffect, useState } from "react";

import homePagePayload from "../../config/homePayload";
import homePageAPI from "../../api/homePageApi";

import CouponFlash from "./CouponFlash";
import { Link } from "react-router-dom";
import { Row, Col, Container } from "react-bootstrap";

import {
  Swiper,
  SwiperSlide,
  Autoplay,
  Navigation,
  Pagination,
} from "./Shared/CustomSwiper";
import { FaBox, FaCheckCircle, FaHeadset } from "react-icons/fa";
import { ArrowRight, BadgePercent, ChevronRight, Clock3, ShieldCheck, Truck, Zap  } from "lucide-react";
import Card from "../brands/Card";

const Product = React.lazy(() => import("../products/Card"));
const Testimonials = React.lazy(() => import("../testimonials/Card"));
const CategoryCard = React.lazy(() => import("../categories/Card"));
const Blog = React.lazy(() => import("../blogs/Card"));
const Banner = React.lazy(() => import("./Banner"));

function Home() {
  const [coupons, setCoupons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [products, setProducts] = useState([]);
  const [topSellerProducts, setTopSellerProducts] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [topBanners, setTopBanners] = useState([]);
  const [middleBanners, setMiddleBanners] = useState([]);
  const firstMiddleBanner = middleBanners[0];
  const secondMiddleBanner = middleBanners[1];
  const [brands, setBrands] = useState([]);
  const [isMarqueeHovered, setIsMarqueeHovered] = useState(false);
const [isMarqueePaused, setIsMarqueePaused] = useState(false);
const [marqueeSpeed, setMarqueeSpeed] = useState(20); 



  useEffect(() => {
    fetchHomePageDetails();
  }, []);

  const CACHE_TIME = 3 * 60 * 1000;

  const applyData = (data) => {
    setTopBanners(data?.banners?.filter((b) => b.position === "top"));
    setMiddleBanners(data?.banners?.filter((b) => b.position === "middle"));
    setCoupons(data?.coupons);
    setCategories(data?.categories);
    setTestimonials(data?.testimonials);
    setBlogs(data?.blogs);
    setProducts(data?.products);
    setTopSellerProducts(data?.topSellerProducts);
    setBrands(data?.brands);
  };

  const fetchHomePageDetails = async () => {
    try {
      const cached = sessionStorage.getItem("homeData");
      const parsed = cached ? JSON.parse(cached) : null;

      if (parsed) applyData(parsed.data);

      if (!parsed || Date.now() - parsed.timestamp > CACHE_TIME) {
        const res = await homePageAPI.homePage(homePagePayload);
        if (res.code === 200) {
          sessionStorage.setItem(
            "homeData",
            JSON.stringify({ data: res.data, timestamp: Date.now() }),
          );
          if (!parsed) applyData(res.data); // only update state if no cache was shown
        }
      }
    } catch (error) {
      console.error("Home API Error:", error);
    }
  };

  const SIDE_OFFERS = [
    {
      id: 1,
      badge: "Quick Ship",
      icon: <Zap size={12} fill="currentColor" />,
      title: "In-Stock Sensors",
      desc: "Get rapid deployment with our most popular thermal sensor models.",
      actionText: "Shop Inventory",
      link: "/shop",
      image: "https://media.istockphoto.com/id/144349803/photo/cables.jpg?s=612x612&w=0&k=20&c=7fht36zJSEXu3j1XQCEC9bMGt4n95AGGkC5GafmHd5o=",
      isLight: false
    },
    {
      id: 2,
      badge: "OEM Solutions",
      icon: <BadgePercent size={14} />,
      title: "Custom Assemblies",
      desc: "Tailored switch and sensor configurations for unique industrial needs.",
      actionText: "Consult Experts",
      link: "/shop",
      image: "https://lifehacker.com/imagery/articles/01HF2GXTQK6CC2AKDEZZHKJ5D5/hero-image.jpg",
      isLight: true
    }
  ];
  
  const TRUST_FEATURES = [
    {
      id: 1,
      icon: <Truck size={20} />,
      title: "Global Logistics",
      desc: "Fast, reliable industrial shipping"
    },
    {
      id: 2,
      icon: <ShieldCheck size={20} />,
      title: "Certified Quality",
      desc: "ISO-verified performance standards"
    },
    {
      id: 3,
      icon: <Clock3 size={20} />,
      title: "Technical Support",
      desc: "24/7 dedicated engineering help"
    },
    {
      id: 4,
      icon: <BadgePercent size={20} />,
      title: "Factory Pricing",
      desc: "Direct-to-business value rates"
    }
  ];

  return (
    <>
      {/* show banners */}
        <section className="luxury-hero-container">
        <div className="container-fluid px-3 px-lg-5">
          <div className="row g-4">
 
            {/* 1. DYNAMIC swiper banner (70% width) */}
            <div className="col-12 col-lg-8">
              <div className="luxury-swiper-viewport">
                {topBanners && topBanners.length > 0 && (
                  <Swiper
                    modules={[Navigation, Autoplay]}
                    slidesPerView={1}
                    autoplay={{ delay: 5000, disableOnInteraction: false }}
                    loop={true}
                  >
                    {topBanners.map((banner, index) => (
                      <SwiperSlide key={banner.id ?? index}>
                        <Banner item={banner} />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                )}
              </div>
            </div>
 
            {/* 2. STATIC side offer cards (30% width) — unchanged */}
            <div className="col-12 col-lg-4 d-flex flex-column gap-3 side-offer-grid">
              {SIDE_OFFERS.map((offer) => (
                <div
                  key={offer.id}
                  className="side-offer-card"
                  style={{ background: offer.isLight ? 'var(--luxury-royal-light, #F0F4F8)' : 'var(--luxury-surface, #FFFFFF)' }}
                >
                  <div className="card-image-thumbnail" style={{ backgroundImage: `url(${offer.image})` }} />
 
                  <div className="card-content-protection">
                    <div className="side-card-tag mb-2" style={{ color: offer.isLight ? 'var(--luxury-royal)' : 'var(--luxury-gold)' }}>
                      {offer.icon} {offer.badge}
                    </div>
                    <h5 style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: '16px', color: 'var(--luxury-royal)', marginBottom: '6px' }}>
                      {offer.title}
                    </h5>
                    <p style={{ fontSize: '12.5px', color: 'var(--luxury-slate)', marginBottom: 0, lineHeight: '1.5' }}>
                      {offer.desc}
                    </p>
                  </div>
 
                  <div className="position-relative z-2">
                    <a
                      href={offer.link}
                      className="fw-semibold mt-3"
                      style={{ fontSize: '13px', color: 'var(--luxury-royal)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                    >
                      {offer.actionText} <ChevronRight size={14} />
                    </a>
                  </div>
                </div>
              ))}
            </div>
 
          </div>
 
          {/* 3. STATIC trust bar — unchanged */}
          <div className="trust-features-bar">
            <div className="row g-3 g-md-4">
              {TRUST_FEATURES.map((feat) => (
                <div key={feat.id} className="col-12 col-sm-6 col-lg-3">
                  <div className="feature-item">
                    <div className="feature-icon-wrapper">{feat.icon}</div>
                    <div>
                      <h6 className="feature-title">{feat.title}</h6>
                      <p className="feature-desc">{feat.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
 
        </div>
      </section>

      {/* coupon listing */}
      {/* Coupon Bar */}
      {coupons && coupons.length > 0 && (
        <div className="coupon-section py-4">
          <div className="container">
            <Suspense fallback={<div></div>}>
              {coupons.map((coupon, index) => (
                <React.Fragment key={index}>
                  <CouponFlash item={coupon}  />
                </React.Fragment>
              ))}
            </Suspense>
          </div>
        </div>
      )}

      {/* category listing */}
      {/* {categories && categories.length > 0 && (
        <section className="custom-category-section main-section">
          <div className="container">
             <div className="section-header-luxury">
            <div>
              <div className="d-flex align-items-center gap-2 mb-2"> */}
                {/* <Sparkles size={14} color="var(--luxury-gold)" /> */}
                {/* <span className="text-uppercase fw-semibold" style={{fontSize: '11px', letterSpacing: '1.5px', color: 'var(--luxury-gold)'}}>
                  Curated Collections
                </span>
              </div>
              <h2 className="section-title-luxury">Our Categeories</h2>
              <p className="section-subtitle-luxury">Seamlessly browse through premium departments verified by global artisans.</p>
            </div>
            
            <a href="#all-categories" className="view-all-luxury-link">
              Browse All Categories <ArrowRight size={15} />
            </a>
          </div>

            <Row className="g-2">
              <Suspense fallback={<div></div>}>
                {categories.map((category, index) => (
                  <Col lg={3} md={6} xs={6} key={index}>
                    <CategoryCard item={category} />
                  </Col>
                ))}
              </Suspense>
            </Row>
          </div>
        </section>
      )} */}

      {/* bestsellers */}
      {products && products.length > 0 && (
        <section className="bestsellers-section main-section">
          <div className="container ">
             <div className="section-header-luxury">
            <div>
              <div className="d-flex align-items-center gap-2 mb-2">
                {/* <Sparkles size={14} color="var(--luxury-gold)" /> */}
                <span className="text-uppercase fw-semibold" style={{fontSize: '11px', letterSpacing: '1.5px', color: 'var(--luxury-gold)'}}>
                  Curated Collections
                </span>
              </div>
              <h2 className="section-title-luxury">Our Products</h2>
              <p className="section-subtitle-luxury">Seamlessly browse through premium departments verified by global artisans.</p>
            </div>
            <a href="/shop" className="view-all-luxury-link">
              Browse All Products <ArrowRight size={15} />
            </a>
           
          </div>
            <Row className="g-3">
              <Suspense fallback={<div></div>}>
                {products.map((product, index) => (
                  <Col lg={3} md={6} sm={6} xs={6} key={[product._id || index]}>
                    <Product item={product} />
                  </Col>
                ))}
              </Suspense>
            </Row>
          </div>
        </section>
      )}

      {/* middle banners */}
      {firstMiddleBanner && (
  <div className="middle-banner">
    <div className="container">

      <Row className="g-0 align-items-center">
        <Col md={12}>
          <div className="middle-banner-img-container">
            {/* <div className="decorative-shape"></div> */}
            <div className="image-parallax-wrapper middle-banner-img-wrapper">
              <img
                src={firstMiddleBanner.image}
                alt="Sanwariya Ornaments"
                className="spotlight-image"
                loading="lazy"
              />
            </div>
          </div>
        </Col>
      </Row>
    </div>
  </div>
)}
      {/* trending */}
      {topSellerProducts && topSellerProducts.length > 0 && (
        <section className="bestsellers-section main-section">
          <div className="container ">
             <div className="section-header-luxury">
            <div>
              <div className="d-flex align-items-center gap-2 mb-2">
                {/* <Sparkles size={14} color="var(--luxury-gold)" /> */}
                <span className="text-uppercase fw-semibold" style={{fontSize: '11px', letterSpacing: '1.5px', color: 'var(--luxury-gold)'}}>
                  Curated Collections
                </span>
              </div>
              <h2 className="section-title-luxury">Our Top Selling Products</h2>
              <p className="section-subtitle-luxury">Seamlessly browse through premium departments verified by global artisans.</p>
            </div>
            
            <a href="/shop" className="view-all-luxury-link">
              Browse All Categories <ArrowRight size={15} />
            </a>
          </div>
            <Row className="g-3">
              <Suspense fallback={<div></div>}>
                {topSellerProducts.map((product, index) => (
                  <Col lg={3} md={6} sm={6} xs={6} key={[product._id || index]}>
                    <Product item={product} />
                  </Col>
                ))}
              </Suspense>
            </Row>
          </div>
        </section>
      )}

      {/* category cta section */}
      {secondMiddleBanner && (
        <section className="cta-banner-2 py-5">
          <Container>
            <Row className="align-items-center">
              {/* LEFT IMAGE */}
              <Col md={7} className="mb-4 mb-md-0">
                <div className="banner-media-wrapper">
                  <img
                    src={secondMiddleBanner.image}
                    alt={secondMiddleBanner.title}
                    className="banner-media"
                    loading="lazy"
                  />
                </div>
              </Col>

              {/* RIGHT CONTENT */}
              <Col md={5}>
                <div className="banner-content-wrapper p-3 p-md-5">
                  {/* TITLE */}
                  <h2 className="main-heading">{secondMiddleBanner.title}</h2>

                  {/* SUBTITLE */}
                  <p className="sub-text">
                    {secondMiddleBanner.subTitle ||
                      secondMiddleBanner.sub_title}
                  </p>

                  {/* CATEGORY SLIDER */}
                  <Link to="/shop" className="btn-luxury-primary">
                    Discover More
                  </Link>
                </div>
              </Col>
            </Row>
          </Container>
        </section>
      )}

      {testimonials && testimonials.length > 0 && (
        <section className="testimonials-section py-5">
          <Container>
            <div className="text-center mb-5 section-header">
               <span className="editorial-badge">Client Musings</span>
              <h2 className="section-title">Customer's Reviews</h2>
            </div>

            <Swiper
              modules={[Navigation, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              navigation={true}
              autoplay={{ delay: 2500 }}
              breakpoints={{
                0: { slidesPerView: 1 },
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              className="testimonial-swiper"
            >
              <Suspense fallback={<div></div>}>
                {testimonials.map((testimonial, index) => (
                  <SwiperSlide key={index}>
                    <Testimonials item={testimonial} />
                  </SwiperSlide>
                ))}
              </Suspense>
            </Swiper>
          </Container>
        </section>
      )}

    {brands && brands.length > 0 && (
  <section className="brand-marquee-section py-4">
    

    <div
      className="brand-marquee-container"
      onMouseEnter={() => setIsMarqueeHovered(true)}
      onMouseLeave={() => setIsMarqueeHovered(false)}
    >
      <div
        className="marquee-track"
        style={{
          animationPlayState: isMarqueePaused || isMarqueeHovered ? "paused" : "running",
          animationDuration: `${marqueeSpeed}s`,
        }}
      >
        {[...brands, ...brands].map((brand, index) => (
          <div key={index} className="brand-item">
            <Card item={brand} />
          </div>
        ))}
      </div>
    </div>
  </section>
)}
      {blogs && blogs.length > 0 && (
        <section className="blog-section py-5">
          <Container>
           <div className="section-header-luxury">
            <div>
              <div className="d-flex align-items-center gap-2 mb-2">
                {/* <Sparkles size={14} color="var(--luxury-gold)" /> */}
                <span className="text-uppercase fw-semibold" style={{fontSize: '11px', letterSpacing: '1.5px', color: 'var(--luxury-gold)'}}>
                  Curated Collections
                </span>
              </div>
              <h2 className="section-title-luxury">Our Blogs</h2>
              <p className="section-subtitle-luxury">Expert perspectives on innovation, craftsmanship, and the future of industrial excellence.</p>
            </div>
            
            <a href="/blog" className="view-all-luxury-link">
              Browse All Blogs <ArrowRight size={15} />
            </a>
          </div>

            <Swiper
              modules={[Pagination, Autoplay]}
              spaceBetween={30}
              slidesPerView={1}
              // autoplay={{ delay: 3500 }}
              pagination={{ clickable: true }}
              breakpoints={{
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              className="blog-swiper"
            >
              <Suspense fallback={<div></div>}>
                {blogs.map((blog, index) => (
                  <SwiperSlide key={blog.id}>
                    <Blog item={blog} />
                  </SwiperSlide>
                ))}
              </Suspense>
            </Swiper>
          </Container>
        </section>
      )}

     
    </>
  );
}

export default Home;
